import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const FILE_TYPE_COLORS = {
    PNG: '#3b82f6', JPG: '#3b82f6', JPEG: '#3b82f6', WEBP: '#3b82f6',
    SVG: '#22c55e',
    PDF: '#ef4444',
    ZIP: '#8b5cf6', RAR: '#8b5cf6',
    AI: '#ff7c00', PSD: '#31a8ff', EPS: '#ff7c00',
};

const getFileColor = (type) => FILE_TYPE_COLORS[(type || '').toUpperCase()] || '#6b7280';

const DownloadIcon = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

const FileIcon = ({ type }) => {
    const color = getFileColor(type);
    return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1" style={{ backgroundColor: hexToRgba(color, 0.06) }}>
            <svg className="w-10 h-10" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{type}</span>
        </div>
    );
};

export default function MediaKitSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'grouped';

    const assetGroups = content.asset_groups || [];
    const brandColors = content.brand_colors || [];
    const showColors = content.show_colors !== false && brandColors.length > 0;

    const [copiedHex, setCopiedHex] = useState(null);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const allItems = assetGroups.flatMap(g => (g.items || []).map(item => ({ ...item, _group: g.group_label })));

    const copyColor = (hex) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(hex).then(() => {
                setCopiedHex(hex);
                setTimeout(() => setCopiedHex(null), 2000);
            });
        }
    };

    // ── Asset card ──
    const AssetCard = ({ item, index }) => (
        <div className={`${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 group ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: visible ? `${index * 80}ms` : '0ms' }}>
            <div className={`${variant === 'visual' ? 'h-48' : 'h-32'} overflow-hidden`}>
                {item.preview_url ? (
                    <img src={item.preview_url} alt={item.label || ''} loading="lazy"
                        className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                        style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }} />
                ) : (
                    <FileIcon type={item.file_type} />
                )}
            </div>
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h4 className="text-sm font-bold truncate" style={{ color: s.text }}>{item.label}</h4>
                        {item.description && variant !== 'visual' && (
                            <p className="text-xs mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                        {item.file_type && (
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: hexToRgba(getFileColor(item.file_type), 0.1), color: getFileColor(item.file_type) }}>
                                {item.file_type}
                            </span>
                        )}
                        {item.file_size && (
                            <span className="text-[10px]" style={{ color: s.textSub }}>{item.file_size}</span>
                        )}
                    </div>
                    {item.download_url && (
                        <a {...resolveLinkProps(item.download_url)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white transition-all hover:opacity-90 hover:scale-105"
                            style={{ backgroundColor: s.accent }}>
                            <DownloadIcon />
                            <span className="hidden sm:inline">Descargar</span>
                        </a>
                    )}
                </div>
            </div>
        </div>
    );

    // ── Brand colors ──
    const BrandColors = () => showColors && (
        <div className="mt-14">
            <h3 className="text-lg font-bold mb-6" style={{ color: s.text }}>
                {content.colors_title || 'Colores de marca'}
            </h3>
            <div className="flex flex-wrap gap-4">
                {brandColors.map((c, i) => (
                    <button key={i} onClick={() => copyColor(c.hex)}
                        className={`group/color transition-all duration-300 hover:scale-105 ${card} overflow-hidden`}
                        style={{ minWidth: 140 }}>
                        <div className="h-20 w-full" style={{ backgroundColor: c.hex }} />
                        <div className="p-3 text-left">
                            <p className="text-sm font-bold" style={{ color: s.text }}>{c.name}</p>
                            <p className="text-xs font-mono mt-0.5" style={{ color: s.textSub }}>
                                {copiedHex === c.hex ? 'Copiado!' : c.hex}
                            </p>
                            {c.usage_note && <p className="text-[10px] mt-1" style={{ color: s.textSub }}>{c.usage_note}</p>}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );

    // ── Grouped variant ──
    if (variant === 'grouped') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    {assetGroups.map((group, gi) => {
                        const items = group.items || [];
                        if (items.length === 0) return null;
                        return (
                            <div key={gi} className="mb-12 last:mb-0">
                                <h3 className="text-lg font-bold mb-5 flex items-center gap-3" style={{ color: s.text }}>
                                    <span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: s.accent }} />
                                    {group.group_label}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {items.map((item, ii) => (
                                        <AssetCard key={ii} item={item} index={gi * 3 + ii} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    <BrandColors />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Flat variant ──
    if (variant === 'flat') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {allItems.map((item, i) => (
                            <AssetCard key={i} item={item} index={i} />
                        ))}
                    </div>
                    <BrandColors />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Visual variant (large previews) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {allItems.map((item, i) => (
                        <AssetCard key={i} item={item} index={i} />
                    ))}
                </div>
                <BrandColors />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
